import { Injectable } from '@angular/core';

export interface DropdownSizes {
  actionType: number;
  inspector: number;
  program: number;
  streetType: number;
}

export interface AppConfig {
  APIBaseUrl: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  clientId: string;
  tenantId: string;
  dropdownSizes: DropdownSizes;
  footerText?: string;
  helpDocumentationUrl: string;
}

export interface TooltipTexts {
  numericOnly: string;
  searchHint: string;
  copyCaseNumber: string;
  openGeneratedLetter: string;
  copyGeneratedPath: string;
  showSelectedPeople: string;
  selectPerson: string;
}

const DEFAULT_TOOLTIP_TEXTS: TooltipTexts = {
  numericOnly: 'Enter numbers only. Max {maxLength} digits.',
  searchHint: 'Type 3 or more characters to search',
  copyCaseNumber: 'Copy',
  openGeneratedLetter: 'Open in Word',
  copyGeneratedPath: 'Copy path',
  showSelectedPeople: 'Show persons',
  selectPerson: 'Select person',
};

function isTooltipTexts(value: unknown): value is TooltipTexts {
  return typeof value === 'object' && value !== null &&
    Object.keys(DEFAULT_TOOLTIP_TEXTS).every((key) =>
      typeof (value as Record<string, unknown>)[key] === 'string',
    );
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config!: AppConfig;
  private tooltipTexts = DEFAULT_TOOLTIP_TEXTS;

  async loadConfig(): Promise<void> {
    const configResponse = await fetch('/config/config.json', { cache: 'no-store' });
    if (!configResponse.ok) {
      throw new Error(`Failed to load app config: ${configResponse.status}`);
    }
    this.config = await configResponse.json();

    try {
      const tooltipResponse = await fetch('/config/tooltip.json', { cache: 'no-store' });
      if (!tooltipResponse.ok) {
        throw new Error(`HTTP ${tooltipResponse.status}`);
      }

      const tooltipTexts: unknown = await tooltipResponse.json();
      if (!isTooltipTexts(tooltipTexts)) {
        throw new Error('Tooltip config has an invalid format');
      }
      this.tooltipTexts = tooltipTexts;
    } catch (error) {
      console.warn(
        'Failed to load tooltip config; using built-in tooltip text. Replace /config/tooltip.json to customize tooltip text.',
        error,
      );
      this.tooltipTexts = DEFAULT_TOOLTIP_TEXTS;
    }
  }

  get(): AppConfig {
    if (!this.config) {
      throw new Error('ConfigService accessed before loadConfig() completed');
    }
    return this.config;
  }

  getTooltipTexts(): TooltipTexts {
    return this.tooltipTexts;
  }
}